import classNames from 'classnames';
import Icon from 'components/icon/icon';
import Img, { ImgProps } from 'components/img/img';
import parse from 'html-react-parser';
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import './teamGridMember.scss';
import 'components/share/shareBar.scss';
import { useLocation } from '@reach/router';
import gsap from 'gsap';
import Links from 'blocks/links/links';
import ButtonLink from 'components/buttonLink/buttonLink';

export interface TeamGridMemberProps {
  title: string;
  content: string;
  slug: string;
  teamMember: {
    position?: string;
    title?: string;
    contactSocial?: {
      platform: string;
      link: string;
    }[];
    cv?: {
      mediaItemUrl: string;
    };
  };
  featuredImage?: {
    node: ImgProps;
  };
  isTop?: boolean;
}

interface Props extends TeamGridMemberProps {
  isActive?: boolean;
  isInActive?: boolean;
  isMobile?: boolean;
  isActiveMobile?: boolean;
}

const TeamGridMember: React.FC<Props> = (props) => {
  const [innerHeight, setInnerHeight] = useState<number>(0);
  const location = useLocation();
  const innerRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleResize = () => {
      const inner = innerRef.current;
      if (!!inner) {
        setInnerHeight(inner.clientHeight);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [props.isActiveMobile]);

  useEffect(() => {
    if (props.isActiveMobile) {
      const main = mainRef.current;
      if (!!main) {
        const mainRect = main.getBoundingClientRect();
        gsap.to(window, {
          duration: 0.4,
          scrollTo: window.scrollY + main.getBoundingClientRect().top - 60 - 16,
        });
      }
    }
  }, [props.isActiveMobile]);

  const renderShareBar = () => {
    return (
      <div className="share-bar">
        <div className="share-bar__row">
          <div className="share-bar__left">
            {props.teamMember.contactSocial?.map((item, key) => (
              <a
                href={item.link.includes('@') && !item.link.includes('mailto:') ? `mailto:${item.link}` : item.link}
                target="_blank"
                rel="norefferer"
                className="share-bar-item"
                key={key}
              >
                <Icon icon={item.platform} size="20" />
              </a>
            ))}
          </div>
          {!!props.teamMember.cv && <ButtonLink file={props.teamMember.cv} label="CV" />}
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames('team-grid-member', {
        'is-active': props.isActive,
        'is-inactive': props.isInActive,
        'is-top': props.isTop,
      })}
      ref={mainRef}
    >
      <div className="team-grid-member__image">
        <div className="team-grid-member__image-inner">{!!props.featuredImage && <Img {...props.featuredImage.node} />}</div>
        {!!props.isTop && props.teamMember.contactSocial?.length && renderShareBar()}
      </div>
      <div className="team-grid-member__content">
        <h4 className="team-grid-member__hl">{props.title}</h4>
        {!!props.teamMember.title && <div className="team-grid-member__title">{props.teamMember.title}</div>}
        {!!props.teamMember.position && <div className="team-grid-member__position">{props.teamMember.position}</div>}
        {!props.isActiveMobile && (
          <a href={`#${props.slug}`} className="team-grid-member__top-link link-text">
            Mehr anzeigen
          </a>
        )}
        {(!!props.isTop || props.isMobile) && (
          <div
            className="team-grid-member__text"
            style={{
              height: props.isMobile ? (props.isActiveMobile ? `${innerHeight}px` : '0') : 'auto',
            }}
          >
            <div className="team-grid-member__text-inner" ref={innerRef}>
              {!!props.isActiveMobile && renderShareBar()}
              {!!props.content && parse(props.content)}
              {!!props.isMobile && (
                <a href={`#${location.pathname.replaceAll('/', '')}`} className="link-text">
                  Schliessen
                </a>
              )}
            </div>
          </div>
        )}
      </div>
      {!props.isTop && !props.isActiveMobile && <a href={`#${props.slug}`} className="team-grid-member__link-area" />}
    </div>
  );
};

export default TeamGridMember;
